import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Autocomplete,
  LinearProgress,
  Chip,
  Divider,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  LocationOn as LocationIcon,
  Group as TeamIcon,
  CheckCircle as CheckIcon,
  PlayArrow as ProcessIcon,
  MyLocation as MyLocationIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../utils/apiClient';
import { schoolCache } from '../../../../../services/cacheService';

const AVAILABLE_GRADES = [
  { id: 'KG', label: 'Kindergarten', shortLabel: 'K' },
  { id: '01', label: 'Grade 1', shortLabel: '1' },
  { id: '02', label: 'Grade 2', shortLabel: '2' },
  { id: '03', label: 'Grade 3', shortLabel: '3' },
  { id: '04', label: 'Grade 4', shortLabel: '4' },
  { id: '05', label: 'Grade 5', shortLabel: '5' },
  { id: '06', label: 'Grade 6', shortLabel: '6' },
  { id: '07', label: 'Grade 7', shortLabel: '7' },
  { id: '08', label: 'Grade 8', shortLabel: '8' },
  { id: '09', label: 'Grade 9', shortLabel: '9' },
  { id: '10', label: 'Grade 10', shortLabel: '10' },
  { id: '11', label: 'Grade 11', shortLabel: '11' },
  { id: '12', label: 'Grade 12', shortLabel: '12' }
];

const NewSchoolWorkflow = () => {
  const { enqueueSnackbar } = useSnackbar();
  
  // Form state
  const [schoolData, setSchoolData] = useState({
    name: '',
    address: {
      street: '',
      city: '',
      state: '',
      zip: ''
    },
    team: null,
    gradesOffered: []
  });
  
  // Processing state
  const [isValidating, setIsValidating] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [processingResults, setProcessingResults] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);

  // AI summary background status: null | 'generating' | 'complete' | 'timeout'
  const [aiSummaryStatus, setAiSummaryStatus] = useState(null);

  // Poll the summary status endpoint until summaries appear or 10 minutes elapse
  useEffect(() => {
    const uuid = processingResults?.school_uuid;
    if (!uuid) return;

    setAiSummaryStatus('generating');
    let done = false;

    const checkStatus = async () => {
      if (done) return;
      try {
        const status = await apiClient.get(`/v2/schools/${uuid}/ai-summary/status`);
        if (status?.has_any_summaries) {
          done = true;
          setAiSummaryStatus('complete');
          clearInterval(intervalId);
          clearTimeout(timeoutId);
        }
      } catch (err) {
        console.warn('AI summary status check failed:', err);
      }
    };

    const intervalId = setInterval(checkStatus, 15000);
    // Safety valve: stop polling after 10 minutes and let user know
    const timeoutId = setTimeout(() => {
      if (!done) {
        clearInterval(intervalId);
        setAiSummaryStatus('timeout');
      }
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [processingResults?.school_uuid]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // Teams data
  const [availableTeams, setAvailableTeams] = useState([]);
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  
  // US States
  const states = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC'
  ];

  // Processing steps
  const steps = [
    'Geocoding',
    'ESRI Data',
    'Nearby Schools',
    'Metrics',
    'AI Summaries',
    'Team Assignment'
  ];

  // Load teams on component mount
  useEffect(() => {
    loadAvailableTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadAvailableTeams = async () => {
    setIsLoadingTeams(true);
    try {
      const response = await apiClient.get('/v2/admin/teams?getAllTeams=true');
      if (response.teams) {
        setAvailableTeams(response.teams);
      }
    } catch (error) {
      console.error('Error loading teams:', error);
      enqueueSnackbar('Error loading teams', { variant: 'error' });
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Handle form field changes
  const handleFieldChange = (field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setSchoolData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setSchoolData(prev => ({
        ...prev,
        [field]: value
      }));
    }
    
    // Clear validation results when form changes
    if (validationResults) {
      setValidationResults(null);
    }
  };

  // Validate form data
  const validateForm = () => {
    const errors = [];
    
    if (!schoolData.name.trim()) {
      errors.push('Location name is required');
    }
    
    if (!schoolData.address.street.trim()) {
      errors.push('Street address is required');
    }
    
    if (!schoolData.address.city.trim()) {
      errors.push('City is required');
    }
    
    if (!schoolData.address.state) {
      errors.push('State is required');
    }
    
    if (!schoolData.address.zip.trim()) {
      errors.push('ZIP code is required');
    }
    
    if (!schoolData.team) {
      errors.push('Team assignment is required');
    }
    
    return errors;
  };

  // Validate address with geocoding
  const validateAddress = async () => {
    const formErrors = validateForm();
    if (formErrors.length > 0) {
      setValidationResults({
        isValid: false,
        errors: formErrors
      });
      return;
    }

    setIsValidating(true);
    
    try {
      // Use the new school address validation endpoint
      const response = await apiClient.post('/v2/schools/validate-address', {
        address: {
          street: schoolData.address.street,
          city: schoolData.address.city,
          state: schoolData.address.state,
          zip: schoolData.address.zip
        }
      });

      if (response.success) {
        setValidationResults({
          isValid: true,
          geocoding: {
            coordinates: response.coordinates,
            formatted_address: response.formatted_address,
            address_components: response.address_components
          },
          schoolData
        });
        enqueueSnackbar('Address validated successfully', { variant: 'success' });
      } else {
        setValidationResults({
          isValid: false,
          errors: [response.error || 'Address validation failed']
        });
        enqueueSnackbar('Address validation failed', { variant: 'error' });
      }
      
    } catch (error) {
      console.error('Error validating address:', error);
      setValidationResults({
        isValid: false,
        errors: ['Error validating address. Please check your input and try again.']
      });
      enqueueSnackbar('Error validating address', { variant: 'error' });
    } finally {
      setIsValidating(false);
    }
  };

  // Process new school
  const processNewSchool = async () => {
    if (!validationResults?.isValid) {
      enqueueSnackbar('Please validate the address first', { variant: 'warning' });
      return;
    }

    setIsProcessing(true);
    setCurrentStep(0);
    
    try {
      // Create the processing request
      const processingRequest = {
        name: schoolData.name,
        address: schoolData.address,
        team_id: schoolData.team.id,
        team_name: schoolData.team.name,
        coordinates: validationResults.geocoding.coordinates,
        grades_offered: schoolData.gradesOffered
      };

      // Call new school processing endpoint
      const response = await apiClient.post('/v2/schools/new', processingRequest);
      
      if (response.success) {
        setProcessingResults(response);
        
        // Simulate step progression
        for (let i = 0; i < steps.length; i++) {
          setCurrentStep(i);
          await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate processing time
        }
        
        // Clear school cache so the new school appears immediately
        try {
          console.log('Clearing school cache after new school creation...');
          await schoolCache.initialize();
          await schoolCache.clearAllCache();
          console.log('School cache cleared successfully');
        } catch (cacheErr) {
          console.warn('Failed to clear school cache:', cacheErr);
          // Don't fail the operation if cache clearing fails
        }
        
        enqueueSnackbar('New school created and processed successfully', { variant: 'success' });
      } else {
        throw new Error(response.error || 'Processing failed');
      }

    } catch (error) {
      console.error('Error processing new school:', error);
      enqueueSnackbar('Error processing new school: ' + error.message, { variant: 'error' });
    } finally {
      setIsProcessing(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setSchoolData({
      name: '',
      address: {
        street: '',
        city: '',
        state: '',
        zip: ''
      },
      team: null,
      gradesOffered: []
    });
    setValidationResults(null);
    setProcessingResults(null);
    setCurrentStep(0);
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 1000, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <AddIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Drop a Pin
            </Typography>
            {isProcessing && (
              <Chip 
                label="Processing..." 
                color="primary" 
                icon={<CircularProgress size={16} />}
              />
            )}
          </Box>
          
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Drop a pin at any location to create a new data point. This workflow will geocode the address,
            collect ESRI demographic data, identify nearby schools, calculate metrics, generate AI geographic
            summaries, and assign to a team.
          </Typography>

          <Alert severity="info">
            <AlertTitle>Processing Overview</AlertTitle>
            Locations will be processed through: Geocoding → ESRI Data → Nearby Schools → Metrics → AI Summaries → Team Assignment.
            <strong> Enrollment projections will not be generated.</strong> AI summaries include geographic analysis only (5, 10, and 15 minute drive times).
          </Alert>
        </CardContent>
      </Card>

      {/* Processing Status */}
      {isProcessing && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Processing Progress
            </Typography>
            <Stepper activeStep={currentStep} alternativeLabel sx={{ mb: 2 }}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <LinearProgress variant="determinate" value={(currentStep / steps.length) * 100} />
          </CardContent>
        </Card>
      )}

      {/* Success Results */}
      {processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <CheckIcon color="success" />
              <Typography variant="h6">
                Location Created Successfully
              </Typography>
            </Box>
            
            <Alert severity="success" sx={{ mb: 2 }}>
              <AlertTitle>Processing Complete</AlertTitle>
              The new location has been created and processed successfully.
            </Alert>

            <Typography variant="body2">
              <strong>Location UUID:</strong> {processingResults.school_uuid}
            </Typography>
            <Typography variant="body2">
              <strong>Location Name:</strong> {processingResults.school_name}
            </Typography>
            <Typography variant="body2">
              <strong>Team Assignment:</strong> {processingResults.team_name}
            </Typography>

            {/* AI Summary background status */}
            <Box
              sx={{
                mt: 2, p: 1.5,
                bgcolor: aiSummaryStatus === 'complete' ? 'success.50' : 'grey.100',
                borderRadius: 1,
                display: 'flex', alignItems: 'flex-start', gap: 1.5
              }}
            >
              {aiSummaryStatus === 'generating' && (
                <>
                  <CircularProgress size={18} sx={{ mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      AI Summary generating in background
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Geographic analysis typically takes 1–2 minutes. This page will update automatically when ready.
                    </Typography>
                  </Box>
                </>
              )}
              {aiSummaryStatus === 'complete' && (
                <>
                  <CheckIcon color="success" sx={{ fontSize: 20, flexShrink: 0 }} />
                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                    AI Summary ready — visible in the AI Summary tool
                  </Typography>
                </>
              )}
              {aiSummaryStatus === 'timeout' && (
                <Typography variant="body2" color="text.secondary">
                  AI Summary is still generating. Check the AI Summary tool in a few minutes.
                </Typography>
              )}
            </Box>

            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={resetForm}>
                Drop Another Pin
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Location Information Form */}
      {!processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Location Information
            </Typography>
            
            <Grid container spacing={3}>
              {/* Location Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location Name"
                  value={schoolData.name}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                  placeholder="e.g., Downtown Plaza, Main Street Campus"
                  required
                />
              </Grid>

              {/* Address Fields */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  Location Address
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={schoolData.address.street}
                  onChange={(e) => handleFieldChange('address.street', e.target.value)}
                  placeholder="123 Main Street"
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={schoolData.address.city}
                  onChange={(e) => handleFieldChange('address.city', e.target.value)}
                  placeholder="City"
                  required
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth required>
                  <InputLabel>State</InputLabel>
                  <Select
                    value={schoolData.address.state}
                    onChange={(e) => handleFieldChange('address.state', e.target.value)}
                    label="State"
                  >
                    {states.map(state => (
                      <MenuItem key={state} value={state}>
                        {state}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="ZIP Code"
                  value={schoolData.address.zip}
                  onChange={(e) => handleFieldChange('address.zip', e.target.value)}
                  placeholder="12345"
                  required
                />
              </Grid>

              {/* Team Assignment */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TeamIcon color="primary" />
                  Team Assignment
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  options={availableTeams}
                  getOptionLabel={(option) => `${option.name} (${option.description || 'No description'})`}
                  value={schoolData.team}
                  onChange={(event, newValue) => handleFieldChange('team', newValue)}
                  loading={isLoadingTeams}
                  isOptionEqualToValue={(option, value) => option.id === value?.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Assign to Team"
                      placeholder="Select a team..."
                      required
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {isLoadingTeams ? <CircularProgress size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2">
                          {option.name}
                        </Typography>
                        {option.description && (
                          <Typography variant="caption" color="text.secondary">
                            {option.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>

              {/* Grades Offered */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" gutterBottom>
                  Grades Offered (Optional)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select all grades that this location offers. Leave empty if not applicable.
                </Typography>
                <FormGroup row>
                  {AVAILABLE_GRADES.map((grade) => (
                    <FormControlLabel
                      key={grade.id}
                      control={
                        <Checkbox
                          checked={schoolData.gradesOffered.includes(grade.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSchoolData(prev => ({
                                ...prev,
                                gradesOffered: [...prev.gradesOffered, grade.id]
                              }));
                            } else {
                              setSchoolData(prev => ({
                                ...prev,
                                gradesOffered: prev.gradesOffered.filter(g => g !== grade.id)
                              }));
                            }
                          }}
                        />
                      }
                      label={grade.shortLabel}
                      sx={{ minWidth: 80 }}
                    />
                  ))}
                </FormGroup>
                
                {/* Quick select buttons */}
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSchoolData(prev => ({
                      ...prev,
                      gradesOffered: ['KG', '01', '02', '03', '04', '05']
                    }))}
                  >
                    Elementary (K-5)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSchoolData(prev => ({
                      ...prev,
                      gradesOffered: ['06', '07', '08']
                    }))}
                  >
                    Middle (6-8)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSchoolData(prev => ({
                      ...prev,
                      gradesOffered: ['09', '10', '11', '12']
                    }))}
                  >
                    High School (9-12)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSchoolData(prev => ({
                      ...prev,
                      gradesOffered: AVAILABLE_GRADES.map(g => g.id)
                    }))}
                  >
                    All Grades (K-12)
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setSchoolData(prev => ({
                      ...prev,
                      gradesOffered: []
                    }))}
                  >
                    Clear All
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Validation Results */}
      {validationResults && !processingResults && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Validation Results
            </Typography>
            
            {validationResults.isValid ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <AlertTitle>Address Validated</AlertTitle>
                <Typography variant="body2">
                  <strong>Formatted Address:</strong> {validationResults.geocoding.formatted_address}
                </Typography>
                {validationResults.geocoding.coordinates && 
                 validationResults.geocoding.coordinates.lat && 
                 validationResults.geocoding.coordinates.lng && (
                  <Typography variant="body2">
                    <strong>Coordinates:</strong> {' '}
                    {validationResults.geocoding.coordinates.lat.toFixed(6)}, {' '}
                    {validationResults.geocoding.coordinates.lng.toFixed(6)}
                  </Typography>
                )}
              </Alert>
            ) : (
              <Alert severity="error" sx={{ mb: 2 }}>
                <AlertTitle>Validation Errors</AlertTitle>
                {validationResults.errors?.map((error, index) => (
                  <Typography key={index} variant="body2">
                    • {error}
                  </Typography>
                ))}
              </Alert>
            )}
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!processingResults && (
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="outlined"
                onClick={validateAddress}
                disabled={isValidating || isProcessing}
                startIcon={isValidating ? <CircularProgress size={20} /> : <MyLocationIcon />}
              >
                {isValidating ? 'Validating...' : 'Validate Address'}
              </Button>
              
              <Button
                variant="contained"
                onClick={processNewSchool}
                disabled={!validationResults?.isValid || isProcessing}
                startIcon={isProcessing ? <CircularProgress size={20} /> : <ProcessIcon />}
                size="large"
              >
                {isProcessing ? 'Processing...' : 'Drop Pin & Process Location'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default NewSchoolWorkflow;
